import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

/** Generic simple list editor used for testimonials & FAQs. */
function useListResource(path) {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await api.get(path);
    setList(data || []);
  };
  useEffect(() => { load(); }, []);

  const create = async (defaults) => {
    const { data } = await api.post(path, defaults);
    setSelected(data);
    load();
  };
  const save = async (extra = {}) => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`${path}/${selected.id}`, { ...selected, ...extra });
      toast.success("Saved");
      setSelected(data);
      load();
    } catch { toast.error("Save failed"); }
  };
  const del = async () => {
    if (!selected || !window.confirm("Delete this item?")) return;
    await api.delete(`${path}/${selected.id}`);
    setSelected(null);
    load();
  };

  return { list, selected, setSelected, create, save, del };
}

export function AdminTestimonials() {
  const { list, selected, setSelected, create, save, del } = useListResource("/admin/testimonials");
  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-end justify-between">
        <div>
          <p className="label-mono opacity-60">Content</p>
          <h1 className="mt-2 font-editorial text-5xl md:text-6xl">Testimonials</h1>
        </div>
        <button className="btn-outline-ink" onClick={() => create({ client_name: "New client", quote: "", service_category: "Portrait", order: 99, published: true })} data-testid="testimonial-new-btn">+ New Testimonial</button>
      </div>
      <div className="mt-10 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-5 border border-ink/15 bg-white divide-y divide-ink/10 max-h-[70vh] overflow-y-auto">
          {list.map((t) => (
            <button key={t.id} onClick={() => setSelected(t)} className={`w-full text-left p-4 hover:bg-ink/5 ${selected?.id === t.id ? "bg-ink/5" : ""}`} data-testid={`testimonial-row-${t.id}`}>
              <p className="font-editorial text-lg">{t.client_name}</p>
              <p className="label-mono opacity-60 mt-1">{t.service_category} · {t.featured ? "Featured" : "—"}</p>
            </button>
          ))}
        </div>
        <div className="col-span-12 md:col-span-7 border border-ink/15 bg-white p-6">
          {selected ? (
            <div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label-mono opacity-60">Client name</label><input className="editorial-input" value={selected.client_name || ""} onChange={(e) => setSelected({ ...selected, client_name: e.target.value })} /></div>
                <div><label className="label-mono opacity-60">Service</label><input className="editorial-input" value={selected.service_category || ""} onChange={(e) => setSelected({ ...selected, service_category: e.target.value })} /></div>
              </div>
              <div className="mt-4"><label className="label-mono opacity-60">Quote</label><textarea rows={5} className="editorial-textarea" value={selected.quote || ""} onChange={(e) => setSelected({ ...selected, quote: e.target.value })} data-testid="testimonial-quote-field" /></div>
              <div className="mt-4"><label className="label-mono opacity-60">Client photo URL</label><input className="editorial-input" value={selected.client_photo || ""} onChange={(e) => setSelected({ ...selected, client_photo: e.target.value })} /></div>
              <div className="mt-4 flex flex-wrap gap-4">
                <label className="flex items-center gap-2 label-mono"><input type="checkbox" checked={!!selected.featured} onChange={(e) => setSelected({ ...selected, featured: e.target.checked })} /> Featured</label>
                <label className="flex items-center gap-2 label-mono"><input type="checkbox" checked={!!selected.published} onChange={(e) => setSelected({ ...selected, published: e.target.checked })} /> Published</label>
              </div>
              <div className="mt-8 flex gap-3">
                <button onClick={() => save()} className="btn-solid" data-testid="testimonial-save">Save</button>
                <button onClick={del} className="btn-outline-ink text-red-700" data-testid="testimonial-delete">Delete</button>
              </div>
            </div>
          ) : <p className="label-mono opacity-60">Select a testimonial.</p>}
        </div>
      </div>
    </div>
  );
}

export function AdminFAQs() {
  const { list, selected, setSelected, create, save, del } = useListResource("/admin/faqs");
  return (
    <div data-testid="admin-faqs">
      <div className="flex items-end justify-between">
        <div>
          <p className="label-mono opacity-60">Content</p>
          <h1 className="mt-2 font-editorial text-5xl md:text-6xl">FAQs</h1>
        </div>
        <button className="btn-outline-ink" onClick={() => create({ question: "New question", answer: "", order: 99, published: true })} data-testid="faq-new-btn">+ New FAQ</button>
      </div>
      <div className="mt-10 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-5 border border-ink/15 bg-white divide-y divide-ink/10 max-h-[70vh] overflow-y-auto">
          {list.map((f) => (
            <button key={f.id} onClick={() => setSelected(f)} className={`w-full text-left p-4 hover:bg-ink/5 ${selected?.id === f.id ? "bg-ink/5" : ""}`} data-testid={`faq-row-${f.id}`}>
              <p className="font-editorial text-lg">{f.question}</p>
              <p className="label-mono opacity-60 mt-1">Order {f.order} · {f.published ? "Published" : "Hidden"}</p>
            </button>
          ))}
        </div>
        <div className="col-span-12 md:col-span-7 border border-ink/15 bg-white p-6">
          {selected ? (
            <div>
              <div><label className="label-mono opacity-60">Question</label><input className="editorial-input" value={selected.question || ""} onChange={(e) => setSelected({ ...selected, question: e.target.value })} data-testid="faq-question" /></div>
              <div className="mt-4"><label className="label-mono opacity-60">Answer</label><textarea rows={6} className="editorial-textarea" value={selected.answer || ""} onChange={(e) => setSelected({ ...selected, answer: e.target.value })} data-testid="faq-answer" /></div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div><label className="label-mono opacity-60">Order</label><input type="number" className="editorial-input" value={selected.order ?? 0} onChange={(e) => setSelected({ ...selected, order: Number(e.target.value) })} /></div>
                <label className="flex items-center gap-2 label-mono mt-6"><input type="checkbox" checked={!!selected.published} onChange={(e) => setSelected({ ...selected, published: e.target.checked })} /> Published</label>
              </div>
              <div className="mt-8 flex gap-3">
                <button onClick={() => save()} className="btn-solid" data-testid="faq-save">Save</button>
                <button onClick={del} className="btn-outline-ink text-red-700" data-testid="faq-delete">Delete</button>
              </div>
            </div>
          ) : <p className="label-mono opacity-60">Select an FAQ.</p>}
        </div>
      </div>
    </div>
  );
}
